import { fetchPostQuery, useQueryPromise } from '/src/api';
import graphQLQueries from './graph-ql-queries';

const WISH_LIST_STATUSES = {
    PRIVATE: 'PRIVATE',
    PUBLIC: 'PUBLIC',
};

const useWishLists = () => {
    const {
        data: wishLists,
        isLoading: isLoadingWishLists,
        runQueryPromise,
        setValues,
    } = useQueryPromise();

    const getWishLists = () => runQueryPromise({
        query: graphQLQueries.wishLists,
        queryKey: ['customer', 'wishlists'],
    });

    const clearWishLists = () => {
        setValues({ data: undefined });
    };

    const isInWishLists = sku => {
        let isInWishList = false;

        wishLists?.forEach(({ items_v2: { items } }) => {
            items.forEach(({ product }) => {
                if (sku === product.sku) {
                    isInWishList = true;
                }
            })
        });

        return isInWishList;
    };

    const createWishList = variables => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query: graphQLQueries.createWishList,
            variables,
        }).then(response => {
            const data = [ ...wishLists ];
            const { createWishlist: { wishlist } } = response;
            data.push(wishlist);

            setValues({ data, isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const deleteWishList = variables => runQueryPromise({
        query: graphQLQueries.deleteWishList,
        queryKey: ['deleteWishlist', 'wishlists'],
        variables,
        isMethodPost: true,
    });

    const updateWishList = variables => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query: graphQLQueries.updateWishList,
            variables,
        }).then(response => {
            const data = [ ...wishLists ];
            const { updateWishlist: { name, uid, visibility } } = response;
            const index = data.findIndex(({ id }) => id === uid);
            data[index].name = name;
            data[index].visibility = visibility;

            setValues({ data, isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const updateStatusProduct = (variables, query) => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query,
            variables,
        }).then(response => {
            const data = [ ...wishLists ];
            const { addProductsToWishlist, removeProductsFromWishlist } = response;
            const { wishlist } = addProductsToWishlist || removeProductsFromWishlist;
            const index = data.findIndex(({ id }) => id === wishlist.id);
            data[index] = wishlist;

            setValues({ data, isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const addProductsToWishList = variables => updateStatusProduct(variables, graphQLQueries.addProductsToWishList);
    const removeProductsFromWishList = variables => updateStatusProduct(variables, graphQLQueries.removeProductsFromWishList);

    const updatePositionProducts = (variables, query) => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query,
            variables,
        }).then(response => {
            const data = [ ...wishLists ];
            const { moveProductsBetweenWishlists, copyProductsBetweenWishlists } = response;
            const { source_wishlist, destination_wishlist } = moveProductsBetweenWishlists || copyProductsBetweenWishlists;

            const destinationIndex = data.findIndex(({ id }) => id === destination_wishlist.id);
            data[destinationIndex] = destination_wishlist;

            if (source_wishlist) {
                const sourceIndex = data.findIndex(({ id }) => id === source_wishlist.id);
                data[sourceIndex] = source_wishlist;
            }

            setValues({ data, isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const moveProductsBetweenWishLists = variables => updatePositionProducts(variables, graphQLQueries.moveProductsBetweenWishLists);
    const copyProductsBetweenWishLists  = variables => updatePositionProducts(variables, graphQLQueries.copyProductsBetweenWishLists);

    return {
        wishLists,
        isWishLists: !!wishLists,
        isLoadingWishLists,
        getWishLists,
        clearWishLists,
        isInWishLists,
        createWishList,
        deleteWishList,
        updateWishList,
        addProductsToWishList,
        removeProductsFromWishList,
        moveProductsBetweenWishLists,
        copyProductsBetweenWishLists,
        WISH_LIST_STATUSES,
    };
};

export default useWishLists;
